import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Card,
  CardBody,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  Skeleton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Button,
  Flex,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Alert,
  AlertIcon,
  SimpleGrid,
} from '@chakra-ui/react'
import { Download } from 'lucide-react'
import ReportPageHeader from '@/components/reports/ReportPageHeader'
import { helpdeskReportService, type ReportFilters } from '@/lib/services/helpdeskReportService'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { enrichBlobAxiosError } from '@/lib/utils/blobDownload'

const PER_PAGE = 25

type SummaryResponse = {
  period: { from: string; to: string }
  counts: {
    raw_fd_rows: number
    raw_fp_rows: number
    tickets_closed: number
    agents_with_ticket_activity: number
    raw_status_rows: number
    attendance_rows: number
  }
  availability: {
    raw_fd: boolean
    raw_fp: boolean
    raw_statuses: boolean
    attendance: boolean
    notes: Record<string, string>
  }
}

export default function AgentProductivityPage() {
  const [filters, setFilters] = useState<ReportFilters>({})
  const [summary, setSummary] = useState<SummaryResponse | null>(null)
  const [pack, setPack] = useState<'fd' | 'fp'>('fd')
  const [page, setPage] = useState(1)
  const [rows, setRows] = useState<Array<Array<string | number | null>>>([])
  const [headers, setHeaders] = useState<string[]>([])
  const [lastPage, setLastPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [loadingSummary, setLoadingSummary] = useState(true)
  const [loadingPreview, setLoadingPreview] = useState(true)
  const [exporting, setExporting] = useState<string | null>(null)
  const { showError, showSuccess } = useToastNotification()

  const previewHeaders = useMemo(
    () => headers.map((h, i) => ({ key: String(i), label: h || `(col ${i + 1})` })).filter((h) => h.label.trim() !== ''),
    [headers]
  )

  useEffect(() => {
    setPage(1)
  }, [filters.from, filters.to, filters.client_id, pack])

  useEffect(() => {
    setLoadingSummary(true)
    helpdeskReportService
      .getAgentProductivity(filters)
      .then((res) => setSummary(res))
      .catch(() => setSummary(null))
      .finally(() => setLoadingSummary(false))
  }, [filters.from, filters.to, filters.client_id, filters.team_id])

  useEffect(() => {
    setLoadingPreview(true)
    helpdeskReportService
      .getAgentProductivityRaw(pack, filters, page, PER_PAGE)
      .then((res) => {
        setHeaders(res.headers ?? [])
        setRows(res.data ?? [])
        setTotal(res.meta?.total ?? 0)
        setLastPage(res.meta?.last_page ?? 1)
      })
      .catch(() => {
        setHeaders([])
        setRows([])
        setTotal(0)
        setLastPage(1)
      })
      .finally(() => setLoadingPreview(false))
  }, [filters.from, filters.to, filters.client_id, filters.team_id, pack, page])

  const downloadPack = async (nextPack: 'full' | 'fd' | 'fp', format: 'excel' | 'csv' = 'excel') => {
    setExporting(nextPack)
    try {
      await helpdeskReportService.download('agent-productivity', filters, format, {
        pack: nextPack,
        timeoutMs: 300_000,
      })
      showSuccess('Download started', `Agent productivity ${nextPack.toUpperCase()} export started.`)
    } catch (err) {
      showError(await enrichBlobAxiosError(err))
    } finally {
      setExporting(null)
    }
  }

  return (
    <Box p={4}>
      <ReportPageHeader
        title="SD Agent Productivity"
        subtitle="Sample-shaped raw extracts (RAW FD / RAW FP) for offline pivots and productivity workbooks"
        reportType="agent-productivity"
        filters={filters}
        onFiltersChange={setFilters}
      />

      <Alert status="info" mb={4} borderRadius="md">
        <AlertIcon />
        <Text fontSize="sm">
          Download the full Excel pack for summary + RAW FD + RAW FP + Statuses/Attendance templates + Contact
          Center Pivot sheet. Use Ticket Productivity for Handling / Daily / Monthly views in-app.
        </Text>
      </Alert>

      <HStack spacing={2} mb={4} flexWrap="wrap">
        <Button
          size="sm"
          colorScheme="blue"
          leftIcon={<Download size={14} />}
          onClick={() => downloadPack('full', 'excel')}
          isLoading={exporting === 'full'}
        >
          Download full workbook (Excel)
        </Button>
        <Button
          size="sm"
          variant="outline"
          leftIcon={<Download size={14} />}
          onClick={() => downloadPack('fd', 'excel')}
          isLoading={exporting === 'fd'}
        >
          RAW FD only
        </Button>
        <Button
          size="sm"
          variant="outline"
          leftIcon={<Download size={14} />}
          onClick={() => downloadPack('fp', 'excel')}
          isLoading={exporting === 'fp'}
        >
          RAW FP only
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => downloadPack('fd', 'csv')}
          isLoading={exporting === 'fd'}
        >
          RAW FD CSV
        </Button>
      </HStack>

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={4}>
        {(
          [
            ['RAW FD rows', summary?.counts.raw_fd_rows],
            ['Tickets closed', summary?.counts.tickets_closed],
            ['RAW FP jobs', summary?.counts.raw_fp_rows],
            ['Agents with activity', summary?.counts.agents_with_ticket_activity],
          ] as const
        ).map(([label, value]) => (
          <Card key={label}>
            <CardBody>
              <Stat>
                <StatLabel fontSize="xs">{label}</StatLabel>
                {loadingSummary ? <Skeleton height="8" mt={2} /> : <StatNumber fontSize="xl">{value ?? 0}</StatNumber>}
              </Stat>
            </CardBody>
          </Card>
        ))}
      </SimpleGrid>

      {(summary?.availability.notes.raw_statuses || summary?.availability.notes.attendance) && (
        <Alert status="warning" mb={4} borderRadius="md">
          <AlertIcon />
          <Box fontSize="sm">
            {summary.availability.notes.raw_statuses && <Text>{summary.availability.notes.raw_statuses}</Text>}
            {summary.availability.notes.attendance && <Text mt={1}>{summary.availability.notes.attendance}</Text>}
          </Box>
        </Alert>
      )}

      <Card>
        <CardBody>
          <Flex justify="space-between" align="center" mb={3} gap={3} flexWrap="wrap">
            <Text fontWeight="600">Raw data preview</Text>
            <Text fontSize="sm" color="gray.500">
              {total.toLocaleString()} rows · page {page} / {lastPage}
            </Text>
          </Flex>

          <Tabs
            index={pack === 'fd' ? 0 : 1}
            onChange={(i) => setPack(i === 0 ? 'fd' : 'fp')}
            variant="enclosed"
            size="sm"
          >
            <TabList>
              <Tab>RAW FD (tickets)</Tab>
              <Tab>RAW FP (field jobs)</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0}>
                <RawPreviewTable loading={loadingPreview} headers={previewHeaders} rows={rows} />
              </TabPanel>
              <TabPanel px={0}>
                <RawPreviewTable loading={loadingPreview} headers={previewHeaders} rows={rows} />
              </TabPanel>
            </TabPanels>
          </Tabs>

          <HStack mt={3} justify="flex-end">
            <Button size="sm" variant="outline" isDisabled={page <= 1 || loadingPreview} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              isDisabled={page >= lastPage || loadingPreview}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </HStack>
        </CardBody>
      </Card>
    </Box>
  )
}

function RawPreviewTable({
  loading,
  headers,
  rows,
}: {
  loading: boolean
  headers: Array<{ key: string; label: string }>
  rows: Array<Array<string | number | null>>
}) {
  const shown = headers.slice(0, 10)

  if (loading) {
    return <Skeleton height="220px" />
  }

  if (rows.length === 0) {
    return (
      <Text fontSize="sm" color="gray.500" py={6}>
        No rows for the selected filters.
      </Text>
    )
  }

  return (
    <Box overflowX="auto">
      <Text fontSize="xs" color="gray.500" mb={2}>
        Showing first {shown.length} columns of {headers.length} (download for full raw layout).
      </Text>
      <Table size="sm">
        <Thead>
          <Tr>
            {shown.map((h) => (
              <Th key={h.key} whiteSpace="nowrap">
                {h.label}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row, idx) => (
            <Tr key={idx}>
              {shown.map((h) => (
                <Td key={h.key} whiteSpace="nowrap" maxW="220px" isTruncated>
                  {String(row[Number(h.key)] ?? '')}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  )
}
